import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, BarChart3, Users, DollarSign, Calendar, ArrowUpRight } from "lucide-react";

export default function SaasAnalytics() {
  const [activeChart, setActiveChart] = useState("students");

  const chartData = {
    students: {
      title: "Student Growth Rate",
      desc: "Track student signups, monthly active counts, and platform churn ratios.",
      metric: "50,289 Active",
      change: "+18.2% YoY",
      svgPath: "M0,60 Q15,40 30,55 T60,25 T90,30 T120,10 T150,15 T180,5 T200,8",
      color: "#8B5CF6", // purple
      glow: "rgba(139, 92, 246, 0.2)"
    },
    exams: {
      title: "Exam Completion Trend",
      desc: "Analyze test completions, speed tests success rates, and average subjective evaluations time.",
      metric: "1.2 Million Taken",
      change: "+24.5% MoM",
      svgPath: "M0,50 Q20,30 40,45 T80,15 T120,20 T160,5 T200,6",
      color: "#3B82F6", // blue
      glow: "rgba(59, 130, 246, 0.2)"
    },
    revenue: {
      title: "Consolidated MRR Tracker",
      desc: "Review recurring fees, white-label upgrade additions, and add-on processing billing history.",
      metric: "₹3,42,500 MRR",
      change: "+12.4% MoM",
      svgPath: "M0,55 Q15,40 30,50 T60,20 T90,22 T120,5 T150,8 T180,2 T200,1",
      color: "#06B6D4", // cyan
      glow: "rgba(6, 182, 212, 0.2)"
    }
  };

  return (
    <section className="py-24 bg-white dark:bg-slate-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            Institutional Analytics
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 font-medium">
            Gain deep insights into academic progress and business performance. Skillzy compiles all student activity logs into high-level visual charts.
          </p>
        </div>

        {/* Dashboard Frame Grid */}
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          
          {/* Chart Controls Left */}
          <div className="lg:col-span-4 space-y-4">
            {Object.keys(chartData).map((key) => {
              const item = chartData[key];
              const isSelected = activeChart === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveChart(key)}
                  className={`w-full flex items-center justify-between p-5 rounded-2xl border text-left transition-all ${
                    isSelected
                      ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white shadow-lg"
                      : "bg-slate-50 dark:bg-slate-900/50 border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-200 dark:hover:border-slate-700"
                  }`}
                >
                  <div className="space-y-1">
                    <h3 className="font-extrabold text-sm">{item.title}</h3>
                    <p className="text-xs opacity-75">{item.desc}</p>
                  </div>
                  <ArrowUpRight size={18} className="shrink-0 ml-4" />
                </button>
              );
            })}
          </div>

          {/* Main Visual Board Right */}
          <div className="lg:col-span-8 bg-slate-50 dark:bg-slate-900/60 rounded-[2.5rem] border border-slate-100 dark:border-slate-850 p-8 shadow-sm">
            
            {/* Chart Card header */}
            <div className="flex flex-wrap justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-6 mb-6 gap-4">
              <div>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Metrics Snapshot</p>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white mt-1">
                  {chartData[activeChart].title}
                </h3>
              </div>
              <div className="flex items-center gap-4 bg-white dark:bg-slate-950 p-3 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase">Total</p>
                  <p className="text-sm font-black text-slate-800 dark:text-white">{chartData[activeChart].metric}</p>
                </div>
                <div className="h-6 w-px bg-slate-100 dark:bg-slate-800" />
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase">Growth</p>
                  <p className="text-sm font-black text-emerald-600 dark:text-emerald-400">{chartData[activeChart].change}</p>
                </div>
              </div>
            </div>

            {/* Visual SVG graph path with glow */}
            <div className="h-64 w-full relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeChart}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="w-full h-full"
                >
                  <svg viewBox="0 0 200 70" className="w-full h-full">
                    <defs>
                      <linearGradient id={`grad-${activeChart}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={chartData[activeChart].color} stopOpacity="0.3"/>
                        <stop offset="100%" stopColor={chartData[activeChart].color} stopOpacity="0"/>
                      </linearGradient>
                    </defs>
                    
                    {/* Simulated background horizontal grid lines */}
                    <line x1="0" y1="15" x2="200" y2="15" stroke="rgba(150,150,150,0.1)" strokeWidth="0.5" />
                    <line x1="0" y1="35" x2="200" y2="35" stroke="rgba(150,150,150,0.1)" strokeWidth="0.5" />
                    <line x1="0" y1="55" x2="200" y2="55" stroke="rgba(150,150,150,0.1)" strokeWidth="0.5" />

                    {/* Chart Line Path */}
                    <motion.path
                      d={chartData[activeChart].svgPath}
                      fill="none"
                      stroke={chartData[activeChart].color}
                      strokeWidth="2"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 1.5, ease: "easeInOut" }}
                    />
                    
                    {/* Area fill path */}
                    <path
                      d={`${chartData[activeChart].svgPath} L 200,70 L 0,70 Z`}
                      fill={`url(#grad-${activeChart})`}
                    />

                    {/* Node points indicators */}
                    <circle cx="200" cy="8" r="2.5" fill={chartData[activeChart].color} className="animate-ping" />
                    <circle cx="200" cy="8" r="1.5" fill={chartData[activeChart].color} />
                  </svg>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Simulated X-axis timestamps */}
            <div className="flex justify-between items-center text-[9px] text-slate-400 font-bold uppercase mt-6 border-t border-slate-100 dark:border-slate-800 pt-4">
              <span>Jan 26</span>
              <span>Feb 26</span>
              <span>Mar 26</span>
              <span>Apr 26</span>
              <span>May 26</span>
              <span>Jun 26</span>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
}
